/**
 * useIdleTimer — tracks user activity and auto-logouts on idle timeout.
 *
 * Events tracked: mousemove, mousedown, click, keydown, scroll, touchstart.
 * Debounces high-frequency events (mousemove, scroll) at 500ms.
 *
 * Multi-tab sync: uses BroadcastChannel API to propagate logout across tabs.
 */
import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimerOptions {
  /** Idle timeout in minutes (from login response) */
  idleTimeoutMinutes: number;
  /** Called when idle timeout fires — should call logout API and redirect */
  onIdle: () => void;
  /** Called to show pre-timeout warning (60s before idle timeout) */
  onWarning?: () => void;
  /** Whether the timer is enabled */
  enabled?: boolean;
}

const WARNING_SECONDS = 60; // Show warning 60 seconds before idle timeout
const HIGH_FREQ_DEBOUNCE_MS = 500; // Debounce mousemove/scroll

export function useIdleTimer({
  idleTimeoutMinutes,
  onIdle,
  onWarning,
  enabled = true,
}: UseIdleTimerOptions) {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef = useRef<boolean>(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    warnedRef.current = false;

    clearAllTimers();

    if (!enabled) return;

    const timeoutMs = idleTimeoutMinutes * 60 * 1000;
    const warningMs = timeoutMs - WARNING_SECONDS * 1000;

    // Set warning timer
    if (onWarning && warningMs > 0) {
      warningTimerRef.current = setTimeout(() => {
        warnedRef.current = true;
        onWarning();
      }, warningMs);
    }

    // Set idle timeout
    idleTimerRef.current = setTimeout(() => {
      onIdle();
    }, timeoutMs);

    lastActivityRef.current = Date.now();
  }, [idleTimeoutMinutes, onIdle, onWarning, enabled, clearAllTimers]);

  // Broadcast logout across tabs
  const broadcastLogout = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.postMessage('logout');
    }
  }, []);

  // Debounced activity handler
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      resetTimer();
    }, HIGH_FREQ_DEBOUNCE_MS);
  }, [resetTimer]);

  // Immediate activity handler (for low-frequency events)
  const handleImmediateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    // Start timer
    resetTimer();

    // Set up event listeners
    const lowFreqEvents: Array<[string, () => void]> = [
      ['mousedown', handleImmediateActivity],
      ['click', handleImmediateActivity],
      ['keydown', handleImmediateActivity],
      ['touchstart', handleImmediateActivity],
    ];

    const highFreqEvents: Array<[string, () => void]> = [
      ['mousemove', handleActivity],
      ['scroll', handleActivity],
    ];

    lowFreqEvents.forEach(([event, handler]) => {
      document.addEventListener(event, handler);
    });

    highFreqEvents.forEach(([event, handler]) => {
      document.addEventListener(event, handler);
    });

    // Multi-tab sync via BroadcastChannel
    try {
      channelRef.current = new BroadcastChannel('session_idle');
      channelRef.current.onmessage = (event) => {
        if (event.data === 'logout') {
          clearAllTimers();
          onIdle();
        }
      };
    } catch {
      // BroadcastChannel not available — no multi-tab sync
    }

    return () => {
      clearAllTimers();

      lowFreqEvents.forEach(([event, handler]) => {
        document.removeEventListener(event, handler);
      });

      highFreqEvents.forEach(([event, handler]) => {
        document.removeEventListener(event, handler);
      });

      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [enabled, resetTimer, handleActivity, handleImmediateActivity, clearAllTimers, onIdle]);

  return {
    resetTimer,
    broadcastLogout,
    isWarningActive: warnedRef.current,
  };
}
